import { t } from 'i18next'
import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'

import { API } from '@/api'
import { TeamMemberVolumeStat } from '@/api/type-response'
import { ServerDomainInfo, Volume } from '@/api/type-server'
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { Loading, Trans } from '@/components'
import DriveVolumeSection from '@/pages/space-mgr/components/drive-volume-section.tsx'
import ExpansionModal from '@/pages/space-mgr/components/expansion-modal'
import ImageVolumeSection from '@/pages/space-mgr/components/image-volume-section.tsx'
import SnapVolumeSection from '@/pages/space-mgr/components/snap-volume-section.tsx'
import { VolumeUsageSegmentedBar } from '@/pages/space-mgr/components/volume-usage-segmented-bar'
import { useTeamStore } from '@/stores/team'
import { formatDatePreset, formatToDynamicDecimalPlaces } from '@/utils/formatter'

export function TeamMemberSpaceDetailPage() {
  const { memberId } = useParams<{ memberId: string }>()
  const location = useLocation()
  const memberName = (location.state as any)?.memberName
  const teamStore = useTeamStore()

  const [loading, setLoading] = useState(true)
  const [expansionModalOpen, setExpansionModalOpen] = useState(false)

  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const [snapshotVolumes, setSnapshotVolumes] = useState<Volume[]>()
  const [cloudVolumes, setCloudVolumes] = useState<Volume[]>()
  const [imageVolumes, setImageVolumes] = useState<Volume[]>()
  const [partVolumeQuotaInGB, setPartVolumeQuotaInGB] = useState(0)
  const [memberVolumeStat, setMemberVolumeStat] = useState<TeamMemberVolumeStat | null>(null)
  const [memberExpireTimeSeconds, setMemberExpireTimeSeconds] = useState<number | null>(null)

  const getDomains = () => {
    API.getDomainList().then((res: any) => {
      if (res.code === 0) {
        const filteredDomainInfos = (res.domainInfos || []).filter((info: any) => info.domain !== 3)
        setDomainInfos(filteredDomainInfos)
      }
    })
  }

  const loadData = async () => {
    if (!memberId) return

    setLoading(true)
    try {
      // Fetch member volumes
      const volumesRes = await API.getMemberVolumes({ res_id: memberId })

      if (volumesRes?.volumes) {
        const volumes = volumesRes.volumes

        // allocPart is the total member storage
        setPartVolumeQuotaInGB(volumes.allocPart?.quota_gb || 0)

        if (volumes.expireTime?.seconds) {
          setMemberExpireTimeSeconds(volumes.expireTime.seconds)
        }

        // subParts are the subdivisions (snapshot/datadrive by region)
        if (volumes.subParts) {
          setSnapshotVolumes(volumes.subParts.filter((v: Volume) => v.type === 2))
          setCloudVolumes(volumes.subParts.filter((v: Volume) => v.type === 1))
          setImageVolumes(volumes.subParts.filter((v: Volume) => v.type === 3))
        }
      }

      // Fetch member volume stats (includes expiration time)
      const teamResID = teamStore.teamTokenData?.teamResID
      if (teamResID) {
        const statsRes = await API.getTeamMemberVolumeStats({
          res_id: teamResID,
          member_res_ids: [memberId],
          page: 1,
          per_page: 1,
        })

        if (statsRes?.volumes && statsRes.volumes.length > 0) {
          setMemberVolumeStat(statsRes.volumes[0])
        }
      }
    } catch (error) {
      console.error('Failed to load member space data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getDomains()
    loadData()
  }, [memberId])

  // Calculate image volume available quota
  const snapshotQuotaSum = (snapshotVolumes || []).reduce((sum, vol) => sum + vol.quota_gb, 0)
  const cloudQuotaSum = (cloudVolumes || []).reduce((sum, vol) => sum + vol.quota_gb, 0)

  const isMemberExpired =
    memberExpireTimeSeconds !== null ? memberExpireTimeSeconds * 1000 < Date.now() : false

  const memberImageOnSpaceBilling = (imageVolumes?.length ?? 0) > 0
  const memberSnapshotOnSpaceBilling = (snapshotVolumes?.length ?? 0) > 0

  const imageVolumeAvailableQuota = Math.max(
    0,
    partVolumeQuotaInGB - snapshotQuotaSum - cloudQuotaSum,
  )

  // Calculate total actual usage from all volumes
  const totalActualUsageInGB = useMemo(() => {
    const allVolumes = [
      ...(snapshotVolumes || []),
      ...(cloudVolumes || []),
      ...(imageVolumes || []),
    ]
    return allVolumes.reduce((sum, volume) => sum + volume.usage_mb / 1024, 0)
  }, [snapshotVolumes, cloudVolumes, imageVolumes])

  return (
    <>
      <div className="card bg-panel-white p-8">
        {/* Header: Back Button + Title */}
        <div className="flex items-center gap-3 mb-8 pb-4 border-b border-neutral-100">
          <Link to="/team/space/admin" className="p-1 bg-panel-gray-100 rounded-md">
            <ArrowLeftIcon className="w-6 h-6 text-tertiary" />
          </Link>
          <div className="h6 gap-2">
            <span className="text-tertiary">
              <Trans i18nKey="space.titleSpaceStorage">Storage Space</Trans>
            </span>
            <span> / </span>
            <span>{memberName || memberId}</span>
          </div>
        </div>

        {/* Storage Overview Section */}
        <div className="border border-neutral-200 p-8 mb-8 rounded-2xl">
          <div className="t-sb mb-4">
            <Trans i18nKey="space.teamStorageSpace">Team Storage Space</Trans>
          </div>

          {partVolumeQuotaInGB === 0 ? (
            <div className="h-2 bg-neutral-100 rounded-full animate-pulse" />
          ) : (
            <VolumeUsageSegmentedBar
              snapshotVolumes={snapshotVolumes}
              cloudVolumes={cloudVolumes}
              totalQuota={partVolumeQuotaInGB}
              imageOnSpaceBilling={memberImageOnSpaceBilling}
              snapshotOnSpaceBilling={memberSnapshotOnSpaceBilling}
            />
          )}

          {/* Usage + Legend */}
          <div className="flex justify-between items-center mt-4">
            <div className="flex gap-4 items-center">
              <div className="text-secondary t-sb">
                <Trans
                  i18nKey="space.usageFormat"
                  values={{
                    usage: formatToDynamicDecimalPlaces(totalActualUsageInGB),
                    quota: formatToDynamicDecimalPlaces(partVolumeQuotaInGB),
                  }}
                  components={[
                    <span className="text-primary" key="usage" />,
                    <span className="text-primary" key="quota" />,
                  ]}
                />
              </div>

              <div className="h-3 w-0.5 bg-neutral-200" />

              {/* Expiration + Upgrade */}
              <div className="flex items-center gap-4 t-sb">
                <div className="text-secondary">
                  <Trans i18nKey="space.expireOn">Expires on</Trans>{' '}
                  <span className="text-primary">
                    {memberVolumeStat?.storageStat?.expireTime
                      ? formatDatePreset(memberVolumeStat.storageStat.expireTime.seconds * 1000)
                      : '-'}
                  </span>
                </div>
                {/* <div className="h-3 w-0.5 bg-neutral-200" />
                <button className="text-highlight t-sb" onClick={() => setExpansionModalOpen(true)}>
                  <Trans i18nKey="space.upgrade">Upgrade</Trans>
                </button> */}
              </div>
            </div>
            {/* Legend */}
            <div className="flex items-center gap-4 t-sm">
              {memberSnapshotOnSpaceBilling && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-btn-tertiary" />
                  <span>
                    <Trans i18nKey="space.volumeItemSubTitleSnapshot">Snapshot</Trans>
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-btn-secondary" />
                <span>
                  <Trans i18nKey="space.volumeItemSubTitleCloudStorage">Datadrive</Trans>
                </span>
              </div>
              {memberImageOnSpaceBilling && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-active" />
                  <span>
                    <Trans i18nKey="space.volumeItemSubTitleImage">Image</Trans>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quota Section */}
        <div>
          {loading ? (
            <div className="w-full flex items-center justify-center p-8">
              <Loading />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {memberImageOnSpaceBilling && (
                <ImageVolumeSection
                  volumes={imageVolumes || []}
                  availableQuota={imageVolumeAvailableQuota}
                  isExpired={isMemberExpired}
                  warningMessages={{
                    critical: t('space.volumeItemMemberNoStorage'),
                    low: t('space.volumeItemMemberLowStorage'),
                  }}
                />
              )}
              {memberSnapshotOnSpaceBilling && (
                <SnapVolumeSection
                  volumes={snapshotVolumes || []}
                  isExpired={isMemberExpired}
                  warningMessages={{
                    critical: t('space.volumeItemMemberNoStorage'),
                    low: t('space.volumeItemMemberLowStorage'),
                  }}
                />
              )}
              <DriveVolumeSection
                volumes={cloudVolumes || []}
                domainInfos={domainInfos}
                isExpired={isMemberExpired}
                warningMessages={{
                  critical: t('space.volumeItemMemberNoStorage'),
                  low: t('space.volumeItemMemberLowStorage'),
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {expansionModalOpen && (
        <ExpansionModal
          open={expansionModalOpen}
          onOk={async () => {
            await loadData()
          }}
          onClose={() => setExpansionModalOpen(false)}
        />
      )}
    </>
  )
}
